import { useState, useRef, useEffect } from "react";
import { Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface TimePickerProps {
  value?: string;
  onChange: (time: string) => void;
  className?: string;
}

export function TimePicker({ value, onChange, className }: TimePickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(value ? parseInt(value.split(":")[0]) : 12);
  const [selectedMinute, setSelectedMinute] = useState(value ? parseInt(value.split(":")[1]) : 0);
  const hourRef = useRef<HTMLDivElement>(null);
  const minuteRef = useRef<HTMLDivElement>(null);

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);

  useEffect(() => {
    if (isOpen && hourRef.current && minuteRef.current) {
      const hourElement = hourRef.current.querySelector(`[data-value="${selectedHour}"]`);
      const minuteElement = minuteRef.current.querySelector(`[data-value="${selectedMinute}"]`);
      
      if (hourElement) {
        hourElement.scrollIntoView({ block: "center", behavior: "instant" });
      }
      if (minuteElement) {
        minuteElement.scrollIntoView({ block: "center", behavior: "instant" });
      }
    }
  }, [isOpen, selectedHour, selectedMinute]);

  const handleSetTime = () => {
    const formattedTime = `${selectedHour.toString().padStart(2, "0")}:${selectedMinute.toString().padStart(2, "0")}`;
    onChange(formattedTime);
    setIsOpen(false);
  };

  const handleSetNow = () => {
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();
    setSelectedHour(currentHours);
    setSelectedMinute(currentMinutes);
    const formattedTime = `${currentHours.toString().padStart(2, "0")}:${currentMinutes.toString().padStart(2, "0")}`;
    onChange(formattedTime);
    setIsOpen(false);
  };

  const displayValue = value || "Select time";

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
            className
          )}
        >
          <Clock className="mr-2 h-4 w-4" />
          {displayValue}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-4 bg-background" align="start">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            {/* Hour Scroll */}
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-2 block text-center">
                Hour
              </label>
              <div
                ref={hourRef}
                className="h-40 overflow-y-auto scroll-smooth snap-y snap-mandatory scrollbar-thin scrollbar-thumb-primary scrollbar-track-muted rounded-md border bg-muted/30"
              >
                {hours.map((hour) => (
                  <div
                    key={hour}
                    data-value={hour}
                    onClick={() => setSelectedHour(hour)}
                    className={cn(
                      "h-10 flex items-center justify-center cursor-pointer snap-center transition-all",
                      selectedHour === hour
                        ? "bg-primary text-primary-foreground font-bold scale-110"
                        : "hover:bg-muted text-muted-foreground"
                    )}
                  >
                    {hour.toString().padStart(2, "0")}
                  </div>
                ))}
              </div>
            </div>

            <span className="text-2xl font-bold">:</span>

            {/* Minute Scroll */}
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-2 block text-center">
                Minute
              </label>
              <div
                ref={minuteRef}
                className="h-40 overflow-y-auto scroll-smooth snap-y snap-mandatory scrollbar-thin scrollbar-thumb-primary scrollbar-track-muted rounded-md border bg-muted/30"
              >
                {minutes.map((minute) => (
                  <div
                    key={minute}
                    data-value={minute}
                    onClick={() => setSelectedMinute(minute)}
                    className={cn(
                      "h-10 flex items-center justify-center cursor-pointer snap-center transition-all",
                      selectedMinute === minute
                        ? "bg-primary text-primary-foreground font-bold scale-110"
                        : "hover:bg-muted text-muted-foreground"
                    )}
                  >
                    {minute.toString().padStart(2, "0")}
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleSetNow}
              className="flex-1"
            >
              Now
            </Button>
            <Button
              onClick={handleSetTime}
              size="sm"
              className="flex-1"
            >
              Set Time
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
